import {invalidateQueries, q} from "../../state";
import {
  ActivityStreamRef,
  AuditPlanPageStatus,
  CreateReviewAction,
  DisclosurePageRef,
  DisclosureSummary,
  EngagementId,
} from "platformed-client/types";
import {platformedClient} from "..";

export async function summary(engagementId: EngagementId): Promise<DisclosureSummary> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/summary",
    {path: {engagement_id: engagementId}},
  );
}

const invalidateSummary = (engagementId: EngagementId) =>
  invalidateQueries([q.tprm.disclosureSummary.filter(engagementId)]);

export async function setPageStatus(
  engagementId: EngagementId,
  page: DisclosurePageRef,
  status: AuditPlanPageStatus,
): Promise<void> {
  await platformedClient.internalRequest(
    "patch",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/pages/status",
    {path: {engagement_id: engagementId}, body: {page, status}},
  );
  await invalidateSummary(engagementId);
}

/** Creates the page's stored state on first use. */
export async function openPageActivityStream(
  engagementId: EngagementId,
  page: DisclosurePageRef,
): Promise<ActivityStreamRef> {
  const stream = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/pages/activity-stream",
    {path: {engagement_id: engagementId}, body: {page}},
  );
  await invalidateSummary(engagementId);
  return stream;
}

export async function lockPages(engagementId: EngagementId, pages: DisclosurePageRef[]): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/pages/lock",
    {path: {engagement_id: engagementId}, body: {pages}},
  );
  await invalidateSummary(engagementId);
}

export async function unlockPages(engagementId: EngagementId, pages: DisclosurePageRef[]): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/pages/unlock",
    {path: {engagement_id: engagementId}, body: {pages}},
  );
  await invalidateSummary(engagementId);
}

export async function createPageReviews(
  engagementId: EngagementId,
  pages: DisclosurePageRef[],
  actions: CreateReviewAction[],
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/pages/reviews",
    {path: {engagement_id: engagementId}, body: {pages, actions}},
  );
  await invalidateQueries([q.tprm.disclosureSummary.filter(engagementId), q.reviewable.filter()]);
}

export async function deletePages(engagementId: EngagementId, pages: DisclosurePageRef[]): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/pages/delete",
    {path: {engagement_id: engagementId}, body: {pages}},
  );
  await invalidateSummary(engagementId);
}
